'use client';

import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '../helpers/cn';

const ExpandableText: React.FC<{
    className?: string;
    style?: React.CSSProperties;
    text?: React.ReactNode;
    initiallyExpanded?: boolean;
    maxLines?: number;
    classNameButton?: string;
    expandOnTextClick?: boolean;
    onClick?: () => void;
}> = ({
    className,
    classNameButton = '',
    style,
    text = '',
    initiallyExpanded,
    maxLines = 3,
    onClick,
    expandOnTextClick = false
}) => {
    const [isExpanded, setIsExpanded] = useState(initiallyExpanded === true);
    const [isLongText, setIsLongText] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);
    const t = useTranslations();

    const handleToggle = useCallback(
        (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
            e.stopPropagation();
            e.preventDefault();
            if (!isExpanded) {
                onClick?.();
            }
            setIsExpanded(!isExpanded);
        },
        [isExpanded, onClick]
    );

    useEffect(() => {
        const el = textRef.current;
        if (el) {
            // Run after layout
            requestAnimationFrame(() => {
                setIsLongText(el.scrollHeight > el.clientHeight);
            });
        }
    }, [text, maxLines]);

    return (
        <div
            className={className}
            style={style}>
            <div
                style={
                    {
                        '--max-lines': isExpanded ? 'none' : maxLines
                    } as React.CSSProperties
                }
                className={cn(
                    'whitespace-pre-wrap',
                    !isExpanded && 'line-clamp-[var(--max-lines)]',
                    expandOnTextClick && 'cursor-pointer'
                )}
                onClick={expandOnTextClick ? handleToggle : undefined}
                ref={textRef}>
                {text}
            </div>
            {isLongText && (
                <button
                    onClick={handleToggle}
                    className={cn(classNameButton, 'opacity-50 hover:opacity-75 lowercase')}>
                    {isExpanded ? t('common.show-less') : t('common.more')}
                </button>
            )}
        </div>
    );
};

export default ExpandableText;
